import React, { FC, useEffect, useMemo, useRef, useState } from "react"
import { useAppDispatch } from "src/redux/storeHooks"
import {
  getUserGalleryAsync,
  uploadUserGalleryAsync,
  deleteUserGalleryAsync,
} from "src/redux/actions/omsProductActions"
import Loader from 'src/utils/Loader'
import { toast } from "react-toastify"
import { getApiErrorMessage } from "src/marketConnect/helpers"
import { validateAndAlertImageFormat } from "src/utils/fileUploadHelpers"
import CommonAlertModal from "src/utils/modals/CommonAlertModal"
import { createPortal } from "react-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrashCan } from "@fortawesome/free-solid-svg-icons"

type Img = {
  fileName: string
  imageUrl: string
  url?: string
  id?: number
  file?: File
  size?: number
  type?: string
  __source?: "server" | "local"
}

type Props = {
  show: boolean
  images?: Img[] // not used; kept for compatibility
  onClose: () => void
  onSelect: (imgs: Img[]) => void // MULTI
  initialSelected?: Img[]
  isSingleSelect?: boolean
}

const SelectProductImageModal: FC<Props> = ({ show, onClose, onSelect, initialSelected = [], isSingleSelect = false }) => {
  const dispatch = useAppDispatch()

  const [q, setQ] = useState("") // applied search (used for API + loadMore)
  const [searchString, setSearchString] = useState("") // typing in input only

  const [serverImages, setServerImages] = useState<Img[]>([])
  const [localImages, setLocalImages] = useState<Img[]>([])
  const [loadingServer, setLoadingServer] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [selectedMap, setSelectedMap] = useState<Record<string, Img>>({})
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [pendingDeleteImg, setPendingDeleteImg] = useState<Img | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const LIMIT = 20

  const keyOf = (img: Img) => {
    if (img.id) return `server-${img.id}`
    const stable = img.url || img.imageUrl
    return `url-${stable}`
  }
  const mapRowsToImgs = (rows: any[]): Img[] =>
    (rows || []).map((x: any) => ({
      id: x?.id ? Number(x.id) : undefined,
      fileName: x?.file_description || `server-${x?.id}`,
      imageUrl: x?.file_path,
      url: x?.file_path,
      __source: "server",
      size: 0,
      type: x?.mime_type || "image/*",
    }))

  const fetchFirstPage = async (search: string) => {
    const s = (search || "").trim()

    setQ(s)
    setServerImages([])
    setPage(1)
    setHasMore(true)

    try {
      setLoadingServer(true)
      const res = await dispatch(getUserGalleryAsync({ page: 1, limit: LIMIT, search: s })).unwrap()
      const rows = res?.results?.data || []
      const list = mapRowsToImgs(rows)

      setServerImages(list)
      setHasMore(rows.length === LIMIT)
      setPage(2)
    } catch {
      setServerImages([])
      setHasMore(false)
    } finally {
      setLoadingServer(false)
    }
  }

  // fetch gallery when modal opens
  useEffect(() => {
    if (!show) return
    //  preselect already chosen images
    const pre: Record<string, Img> = {}
      ; (initialSelected || []).forEach((img) => {
        pre[keyOf(img)] = img
      })
    setSelectedMap(pre)
    setLocalImages([])
    setSearchString("")
    fetchFirstPage("")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const loadMore = async () => {
    if (!hasMore || loadingMore || loadingServer) return

    try {
      setLoadingMore(true)
      const res = await dispatch(getUserGalleryAsync({ page, limit: LIMIT, search: q })).unwrap()
      const rows = res?.results?.data || []
      const list = mapRowsToImgs(rows)

      setServerImages((prev) => {
        const existing = new Set(prev.map((p) => p.id))
        const unique = list.filter((x) => !x.id || !existing.has(x.id))
        return [...prev, ...unique]
      })

      setHasMore(rows.length === LIMIT)
      setPage((p) => p + 1)
    } finally {
      setLoadingMore(false)
    }
  }

  const allImages = useMemo(() => [...localImages, ...serverImages], [localImages, serverImages])

  const filtered = useMemo(() => {
    if (!searchString.trim()) return allImages;
    const s = searchString.toLowerCase();
    return allImages.filter(img => (img.fileName || "").toLowerCase().includes(s));
  }, [allImages, searchString])

  const onClickAddNew = () => fileInputRef.current?.click()

  const onPickFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return

    const picked = Array.from(files)
    const newFiles = picked.filter(file => {
      if (!validateAndAlertImageFormat(file)) return false;

      const isDuplicate = allImages.some(img => img.fileName === file.name && (img.size === file.size || img.size === 0));
      if (isDuplicate) {
        toast.warning(`Image ${file.name} is already uploaded or selected`);
        return false;
      }
      return true;
    });

    if (newFiles.length === 0) return;

    const mapped: Img[] = newFiles.map((file) => ({
      file,
      fileName: file.name,
      size: file.size,
      type: file.type,
      imageUrl: URL.createObjectURL(file),
      __source: "local",
    }))

    setLocalImages((prev) => [...mapped, ...prev])
  }

  // cleanup blob urls on unmount
  useEffect(() => {
    return () => {
      localImages.forEach((img) => {
        if (img.imageUrl?.startsWith("blob:")) URL.revokeObjectURL(img.imageUrl)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleSelect = (img: Img) => {
    const k = keyOf(img)
    setSelectedMap((prev) => {
      if (isSingleSelect) {
        if (prev[k]) return {}
        return { [k]: img }
      }
      const copy = { ...prev }
      if (copy[k]) delete copy[k]
      else copy[k] = img
      return copy
    })
  }

  const selectedList = useMemo(() => Object.values(selectedMap), [selectedMap])

  const uploadSelectedLocalFiles = async (locals: Img[]): Promise<Img[]> => {
    if (!locals.length) return []

    const fd = new FormData()
    locals.forEach((img) => {
      if (img.file instanceof File) fd.append("file", img.file)
    })

    const uploadRes = await dispatch(uploadUserGalleryAsync(fd)).unwrap()
    const uploadedRows = uploadRes?.data || []

    const uploadedImgs: Img[] = uploadedRows.map((u: any) => {
      const finalUrl = u?.file_url || u?.file_path || u?.url
      const finalName = u?.filename || u?.file_description || "uploaded"
      return {
        fileName: finalName,
        imageUrl: finalUrl,
        url: finalUrl,
        __source: "server",
        size: 0,
        type: "image/*",
      }
    })

    return uploadedImgs.filter((x) => x.imageUrl)
  }

  const handleUploadClick = async () => {
    if (!selectedList.length) {
      if (isSingleSelect) {
        onSelect([])
        onClose()
      }
      return
    }

    const selectedServer = selectedList.filter((x) => x.__source === "server" || x.url)
    const selectedLocal = selectedList.filter((x) => x.__source === "local" && x.file instanceof File)

    try {
      setUploading(true)
      const uploaded = selectedLocal.length ? await uploadSelectedLocalFiles(selectedLocal) : []
      onSelect([...selectedServer, ...uploaded])
      onClose()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, "Failed to upload image(s)"))
    } finally {
      setUploading(false)
    }
  }

  const askDelete = (img: Img) => {
    if (!img?.id || img.__source === "local") return
    setPendingDeleteImg(img)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirmed = async () => {
    if (!pendingDeleteImg?.id) return

    try {
      setDeleting(true)

      const res = await dispatch(deleteUserGalleryAsync({ id: pendingDeleteImg.id })).unwrap()
      const status = Number(res?.status ?? 200)
      const msg = res?.errors?.detail || res?.message

      if (status !== 200) {
        toast.error(msg || "Failed to delete image")
        return
      }

      toast.success(msg || "Image deleted")

      setServerImages((prev) => prev.filter((x) => x.id !== pendingDeleteImg.id))

      const k = keyOf(pendingDeleteImg)
      setSelectedMap((prev) => {
        if (!prev[k]) return prev
        const copy = { ...prev }
        delete copy[k]
        return copy
      })
    } catch (error: any) {
      const err = error?.data ?? error
      toast.error(err?.errors?.detail || err?.message || "Failed to delete image")
    } finally {
      setDeleting(false)
      setPendingDeleteImg(null)
      setShowDeleteModal(false)
    }
  }

  if (!show) return null

  const modalJSX = (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 999, // IMPORTANT: higher than any page/card
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 6,
        }}
        onClick={onClose}
      >
        <div
          style={{
            width: "min(60rem, 100%)",
            maxHeight: "95vh",
            background: "#fff",
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ fontWeight: 700 }}>
              {isSingleSelect ? "Select Product Image (Single Selection)" : "Select Product Images (Multi Selection)"}{" "}
              <span style={{ fontWeight: 500, color: "#6b7280" }}>
                ({selectedList.length} selected)
              </span>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                border: "none",
                background: "transparent",
                fontSize: 22,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {/* Search row + Add new */}
          <div
            style={{
              padding: 16,
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              gap: 10,
              alignItems: "center",
            }}
          >
            <div className="position-relative" style={{ width: "20rem" }}>
              {searchString === "" && (
                <i
                  className="bi bi-arrow-return-left position-absolute top-50 translate-middle-y end-0 me-2 text-secondary"
                  style={{ pointerEvents: "none" }}
                />
              )}

              <input
                className="form-control"
                type="search"
                placeholder="Search"
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    fetchFirstPage(searchString)
                  }
                }}
              />
            </div>

            <button type="button" className="btn btn-primary" onClick={onClickAddNew}>
              + Add New Images
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => onPickFiles(e.target.files)}
            />
          </div>

          {/* Grid (infinite scroll) */}
          <div
            onScroll={(e) => {
              const el = e.currentTarget
              const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80
              if (nearBottom) loadMore()
            }}
            style={{ padding: 16, overflowY: "auto", maxHeight: "calc(85vh - 120px)" }}
          >
            {loadingServer ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                <Loader variant="section" />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ color: "#6b7280" }}>No images found.</div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: 16,
                }}
              >
                {filtered.map((img) => {
                  const k = keyOf(img)
                  const isSelected = !!selectedMap[k]

                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => toggleSelect(img)}
                      style={{
                        border: "1px solid #e5e7eb",
                        outline: isSelected ? "2px solid #2563eb" : "none",
                        outlineOffset: 2,
                        borderRadius: 10,
                        background: "#fff",
                        padding: 10,
                        textAlign: "left",
                        cursor: "pointer",
                        position: "relative",
                      }}
                    >
                      {/* delete (server only) */}
                      {img.__source === "server" && img.id && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            askDelete(img)
                          }}
                          title="Delete"
                          style={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            width: 22,
                            height: 22,
                            borderRadius: 999,
                            border: "none",
                            background: "#dc3545",
                            color: "#fff",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 1,
                            fontSize: 12,
                            zIndex: 2,
                          }}
                        >
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                      )}

                      <div
                        style={{
                          height: 100,
                          borderRadius: 8,
                          overflow: "hidden",
                          background: "#f9fafb",
                          position: "relative",
                        }}
                      >
                        <img
                          src={img.url || img.imageUrl}
                          alt={img.fileName}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />

                        {img.__source === "local" && (
                          <div
                            style={{
                              position: "absolute",
                              left: 8,
                              bottom: 8,
                              background: "rgba(0,0,0,0.6)",
                              color: "#fff",
                              fontSize: 11,
                              padding: "2px 6px",
                              borderRadius: 999,
                            }}
                          >
                            New
                          </div>
                        )}

                        {isSelected && (
                          <div
                            style={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              background: "#2563eb",
                              color: "#fff",
                              borderRadius: 999,
                              fontSize: 12,
                              padding: "2px 8px",
                            }}
                          >
                            ✓
                          </div>
                        )}
                      </div>

                      <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, wordBreak: "break-word" }}>
                        {img.fileName}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}

            {loadingMore && (
              <div className="d-flex justify-content-center py-3">
                <Loader variant="section" />
              </div>
            )}

            {!loadingServer && !loadingMore && !hasMore && serverImages.length > 0 && (
              <div className="text-center py-2" style={{ color: "#6b7280", fontSize: 12 }}>
                No more images
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: 6,
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
            }}
          >
            <button type="button" className="btn btn-light" onClick={onClose} disabled={uploading}>
              Cancel
            </button>

            <button
              type="button"
              className="btn btn-primary"
              disabled={(!isSingleSelect && selectedList.length === 0) || uploading}
              onClick={handleUploadClick}
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      </div>

      <CommonAlertModal
        title="Delete Item?"
        subtitle="This image will be removed from the Gallery."
        isVisible={showDeleteModal}
        isTermsVisible={false}
        onSuccess={handleDeleteConfirmed}
        onCancel={() => {
          setPendingDeleteImg(null)
          setShowDeleteModal(false)
        }}
        successBtnTitle={deleting ? "Deleting..." : "Delete"}
        cancelBtnTitle="Cancel"
      />
    </>
  )

  return createPortal(modalJSX, document.body)
}

export default SelectProductImageModal
